/**
 * Deep-diff two JSON values and produce annotated lines for side-by-side display.
 *
 * Each returned line has:
 *   { left: string|null, right: string|null, type: 'same'|'added'|'removed'|'changed' }
 *
 * The approach: pretty-print both JSON objects, then run a longest-common-subsequence (LCS)
 * diff on the resulting lines. This gives a natural, human-readable side-by-side view
 * that mirrors how developers read JSON, rather than a structural path-based diff.
 */

/**
 * Compute LCS table for two arrays of strings.
 */
function lcsTable(a, b) {
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  return dp;
}

/**
 * Backtrack through LCS table to produce diff operations.
 * Returns array of { type, leftIdx, rightIdx }.
 */
function backtrack(dp, a, b) {
  const ops = [];
  let i = a.length;
  let j = b.length;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      ops.push({ type: 'same', leftIdx: i - 1, rightIdx: j - 1 });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      ops.push({ type: 'added', rightIdx: j - 1 });
      j--;
    } else {
      ops.push({ type: 'removed', leftIdx: i - 1 });
      i--;
    }
  }

  return ops.reverse();
}

/**
 * Diff two JSON values and return annotated lines for side-by-side rendering.
 *
 * @param {*} left  - The "old" JSON value (version A)
 * @param {*} right - The "new" JSON value (version B)
 * @returns {{ lines: Array<{left: string|null, right: string|null, type: string}>, stats: {added: number, removed: number, changed: number, same: number} }}
 */
export function diffJson(left, right) {
  const leftLines = JSON.stringify(left, null, 2).split('\n');
  const rightLines = JSON.stringify(right, null, 2).split('\n');

  const dp = lcsTable(leftLines, rightLines);
  const ops = backtrack(dp, leftLines, rightLines);

  const lines = [];
  let added = 0;
  let removed = 0;
  let changed = 0;
  let same = 0;

  // Merge adjacent removed+added into "changed" pairs
  let i = 0;
  while (i < ops.length) {
    const op = ops[i];

    if (op.type === 'same') {
      lines.push({
        left: leftLines[op.leftIdx],
        right: rightLines[op.rightIdx],
        type: 'same',
        leftNum: op.leftIdx + 1,
        rightNum: op.rightIdx + 1,
      });
      same++;
      i++;
    } else if (op.type === 'removed') {
      // Look ahead for a matching 'added' to pair as 'changed'
      if (i + 1 < ops.length && ops[i + 1].type === 'added') {
        lines.push({
          left: leftLines[op.leftIdx],
          right: rightLines[ops[i + 1].rightIdx],
          type: 'changed',
          leftNum: op.leftIdx + 1,
          rightNum: ops[i + 1].rightIdx + 1,
        });
        changed++;
        i += 2;
      } else {
        lines.push({
          left: leftLines[op.leftIdx],
          right: null,
          type: 'removed',
          leftNum: op.leftIdx + 1,
          rightNum: null,
        });
        removed++;
        i++;
      }
    } else if (op.type === 'added') {
      lines.push({
        left: null,
        right: rightLines[op.rightIdx],
        type: 'added',
        leftNum: null,
        rightNum: op.rightIdx + 1,
      });
      added++;
      i++;
    } else {
      i++;
    }
  }

  return { lines, stats: { added, removed, changed, same } };
}
